import { useState } from 'react';
import { GoalsEditor } from '@/features/events/GoalsEditor';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Pencil,
} from 'lucide-react';
import dayjs from 'dayjs';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { ProjectStatus, XkEvent } from '@/lib/api/types';
import { Skeleton } from '@/components/ui/Skeleton';
import { ExportXlsxButton } from '@/components/ui/ExportXlsxButton';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { EntityTypeBadge } from '@/components/ui/EntityTypeBadge';
import { brandsApi } from '@/features/brands/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { usePlanQuota } from '@/lib/permissions/usePlanQuota';
import { useFormPanel } from '@/components/layout/formPanelStore';
import { DesignJourneySection } from '@/features/events/design-journey/DesignJourneySection';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { PlannerTab } from './planner/PlannerTab';
import { TrackTab } from './track/TrackTab';
import { MeasureTab } from './measure/MeasureTab';
import { CompareTab } from './compare/CompareTab';

/**
 * ProjectDetailPage — the inside of a "Project" (= xk-event-group).
 *
 * Lists the events under the group + create-event form. Stations + stock
 * live one level deeper (under each event).
 */
const STATUS_KEY: Record<ProjectStatus, { color: string; key: string }> = {
  planning: { color: 'var(--color-warn)', key: 'project.status.planning' },
  live: { color: 'var(--color-xk-accent)', key: 'project.status.live' },
  wrapped: { color: 'var(--color-xk-text-muted)', key: 'project.status.wrapped' },
};

type ProjectTab = 'plan' | 'journey' | 'track' | 'measure' | 'compare';

/**
 * The tab ids that exist. `measure` is the Report tab — the label was renamed
 * for the UI, the identifier deliberately was not, because it is also the
 * shape of every saved link.
 */
const PROJECT_TABS: ProjectTab[] = ['plan', 'journey', 'track', 'measure', 'compare'];

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const navigate = useNavigate();
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  // Project editing happens in the right rail, not a modal.
  const openForm = useFormPanel((s) => s.openForm);
  const closeForm = useFormPanel((s) => s.closeForm);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const eventQuota = usePlanQuota('xotskiEvents');

  // An unrecognised `?tab=` used to render nothing at all: no panel, no tab
  // highlighted, just the page header above empty space. A stale bookmark or a
  // typo should land on the first tab, not on a dead screen.
  const requestedTab = searchParams.get('tab') as ProjectTab | null;
  const tab: ProjectTab =
    requestedTab && PROJECT_TABS.includes(requestedTab) ? requestedTab : 'plan';
  const focusEventId = searchParams.get('focusEvent')
    ? Number(searchParams.get('focusEvent'))
    : null;
  const setTab = (next: ProjectTab) => {
    // Whatever the rail is editing belongs to a selection made on the tab you
    // are leaving. Picking an event on PLAN and then switching to TRACK used to
    // leave its properties form open beside a list it has nothing to do with —
    // the rail is contextual, and context ends at the tab boundary.
    closeForm({ collapse: true });
    const p = new URLSearchParams(searchParams);
    p.set('tab', next);
    if (next !== 'plan') p.delete('focusEvent');
    setSearchParams(p, { replace: true });
  };
  // TRACK → PLAN deep-jump: open the PLAN tab focused on a specific event.
  const jumpToPlan = (eventId: number) => {
    const p = new URLSearchParams(searchParams);
    p.set('tab', 'plan');
    p.set('focusEvent', String(eventId));
    setSearchParams(p, { replace: true });
  };

  const groupQ = useQuery({
    queryKey: queryKeys.eventGroups.detail(groupId),
    queryFn: () => eventsApi.getGroup(groupId),
    enabled: Number.isFinite(groupId),
  });
  useFavoritable(groupQ.data ? `Project · ${groupQ.data.name}` : null);
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byGroup(groupId),
    queryFn: () => eventsApi.listEvents({ eventGroupId: groupId }),
    enabled: Number.isFinite(groupId),
  });

  // Caso 16 — Breadcrumb chain reads brand name lazily so the header is
  // navigable even before the brand fetch resolves.
  const brandId = groupQ.data?.companyId ?? 0;
  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId),
    queryFn: () => brandsApi.get(brandId),
    enabled: brandId > 0,
  });

  const group = groupQ.data ?? null;
  const events = eventsQ.data?.items ?? [];
  // Project status/range prefer the now-authoritative group columns and fall
  // back to deriving from child events for projects created before the range
  // feature shipped.
  const derivedStatus = deriveProjectStatus(events);
  const projectStatus: ProjectStatus = group?.projectStatus ?? derivedStatus;
  const status = STATUS_KEY[projectStatus] ?? STATUS_KEY.planning;
  const { startDate, endDate } = group
    ? {
        startDate: group.startDate ?? minEventDate(events, 'startDate'),
        endDate: group.endDate ?? maxEventDate(events, 'endDate'),
      }
    : { startDate: null, endDate: null };

  if (groupQ.isLoading) return <div className="page"><Skeleton className="h-40" /></div>;
  if (!group) return <p className="page text-sm text-[var(--color-fg-muted)]">{t('project.notFound')}</p>;

  return (
    <div className="page space-y-6">
      <Breadcrumbs
        items={[
          {
            label: brandQ.data?.name ?? t('breadcrumbs.brand'),
            to: group.companyId ? `/brand/${group.companyId}` : undefined,
          },
          { label: group.name ?? t('breadcrumbs.project') },
        ]}
      />
      <button
        type="button"
        onClick={() => (group.companyId ? navigate(`/brand/${group.companyId}`) : navigate('/'))}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('project.backToBrand')}
      </button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span
              className="rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#0A0A0A]"
              style={{ backgroundColor: status.color, fontFamily: 'var(--font-display)' }}
            >
              {t(status.key)}
            </span>
            {group.codeRef && (
              <span
                className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {group.codeRef}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <EntityTypeBadge type="project" />
            <h1
              className="text-3xl font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {group.name}
            </h1>
            <HelpHint section="projects" />
          </div>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-[var(--color-xk-text-muted)]">
            <Calendar className="h-3.5 w-3.5" />
            {fmtRange(startDate, endDate, t('project.tbd'))}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportXlsxButton
            filename={`events-project-${groupId}`}
            sheetName="Events"
            disabled={events.length === 0}
            rows={() => events.map(eventToExportRow)}
          />
          {/* «Ver tendencias» oculto a petición (sept 2026). La pantalla de
              tendencias sigue viva en /projects/:id/trends y en la marca. */}
          <button
            type="button"
            data-tour="project-edit"
            onClick={() =>
              openForm({
                kind: 'project-edit',
                mode: 'edit',
                title: t('project.edit.title'),
                context: { group },
                onDone: (result) => {
                  if ((result as { deleted?: boolean } | undefined)?.deleted) {
                    navigate(group.companyId ? `/brand/${group.companyId}` : '/');
                  }
                },
              })
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-accent-ink)]"
          >
            <Pencil className="h-3.5 w-3.5" />
            {t('project.editProject')}
          </button>
        </div>
      </header>


      <Tabs value={tab} onValueChange={(v) => setTab(v as ProjectTab)}>
        <TabsList data-tour="project-tabs">
          <TabsTrigger value="plan">{t('project.tab.plan')}</TabsTrigger>
          <TabsTrigger value="journey">{t('designJourney.tabLabel')}</TabsTrigger>
          <TabsTrigger value="track">{t('project.tab.track')}</TabsTrigger>
          <TabsTrigger value="measure">{t('project.tab.measure')}</TabsTrigger>
          <TabsTrigger value="compare">{t('project.tab.compare')}</TabsTrigger>
        </TabsList>

        <TabsContent value="plan">
          {/* CH-08 — metas del proyecto: sus eventos las heredan enteras. */}
          <GoalsEditor scope="project" id={groupId} className="mb-4" />
          <PlannerTab
            groupId={groupId}
            brandId={brandId}
            companyId={group.companyId ?? undefined}
            projectStart={startDate}
            projectEnd={endDate}
            events={events}
            totalEventCount={events.length}
            focusEventId={focusEventId}
            onLimitReached={() => setUpgradeOpen(true)}
          />
        </TabsContent>

        {/*
          El MISMO Design Journey que el de un evento, con el alcance abierto a
          todos los del proyecto.

          Antes había aquí un editor aparte que escribía `bs_event` y lo
          cascadeaba a los eventos hermanos. Ese modelo murió cuando lo que ve
          el invitado pasó a salir del despliegue de Xurvey: el editor seguía
          en pie, con su propio aviso diciendo que lo que se tocara en él «ya no
          llega al enlace público». Un editor que anuncia que no hace nada es
          peor que no tenerlo — ocupa el sitio del que sí funciona.
        */}
        <TabsContent value="journey">
          <DesignJourneySection
            eventIds={events.map((e) => e.id)}
            eventNames={Object.fromEntries(events.map((e) => [e.id, e.name]))}
          />
        </TabsContent>

        <TabsContent value="track">
          <TrackTab groupId={groupId} events={events} onJumpToPlan={jumpToPlan} />
        </TabsContent>

        <TabsContent value="measure">
          <MeasureTab
            groupId={groupId}
            brandId={brandId}
            projectName={group.name}
            events={events}
            startDate={startDate}
            endDate={endDate}
          />
        </TabsContent>

        {/* COMPARAR — los eventos del proyecto unos contra otros, con margen y test. */}
        <TabsContent value="compare">
          <CompareTab groupId={groupId} events={events} />
        </TabsContent>
      </Tabs>

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="xotskiEvents"
        currentPlanName="Free"
        limit={eventQuota.limit}
      />
    </div>
  );
}

/**
 * `<input type="datetime-local">` gives back a string like "2026-03-24T07:00"
 * (local time, no zone). Convert to a real ISO 8601 timestamp so the backend
 * stores a definite point in time. Empty strings become null.
 */
function eventToExportRow(e: XkEvent): Record<string, unknown> {
  return {
    ID: e.id,
    Name: e.name,
    Address: e.address ?? '',
    'Starts at': e.startDate ?? '',
    'Ends at': e.endDate ?? '',
    Sections: e.sectionCount ?? '',
    'Code ref': e.codeRef ?? '',
    Status: e.eventStatus ?? '',
    'Project ID': e.eventGroupId,
  };
}

/**
 * Backend `xk_event_group` does not expose projectStatus / startDate / endDate.
 * Derive them from the child events so the UI shows real values:
 *   - status: live if any event currently in [start,end]; wrapped if all are past;
 *     planning otherwise (no events, no dates, or all future)
 *   - startDate: min(events.startDate)
 *   - endDate:   max(events.endDate)
 */
function deriveProjectStatus(events: XkEvent[]): ProjectStatus {
  if (events.length === 0) return 'planning';
  const now = Date.now();
  let hasLive = false;
  let allWrapped = true;
  for (const e of events) {
    if (e.eventStatus !== 'Active') continue;
    const start = e.startDate ? new Date(e.startDate).getTime() : null;
    const end = e.endDate ? new Date(e.endDate).getTime() : null;
    if (start != null && end != null && now >= start && now <= end) hasLive = true;
    if (end != null && now <= end) allWrapped = false;
    if (start == null || end == null) allWrapped = false;
  }
  if (hasLive) return 'live';
  if (allWrapped) return 'wrapped';
  return 'planning';
}

function minEventDate(events: XkEvent[], field: 'startDate' | 'endDate'): string | null {
  const ts = events
    .map((e) => e[field])
    .filter((d): d is string => !!d)
    .map((d) => new Date(d).getTime())
    .filter((t) => Number.isFinite(t));
  if (ts.length === 0) return null;
  return new Date(Math.min(...ts)).toISOString();
}

function maxEventDate(events: XkEvent[], field: 'startDate' | 'endDate'): string | null {
  const ts = events
    .map((e) => e[field])
    .filter((d): d is string => !!d)
    .map((d) => new Date(d).getTime())
    .filter((t) => Number.isFinite(t));
  if (ts.length === 0) return null;
  return new Date(Math.max(...ts)).toISOString();
}

function fmtRange(start: string | null | undefined, end: string | null | undefined, tbd: string): string {
  if (!start) return tbd;
  const s = dayjs(start);
  const e = end ? dayjs(end) : null;
  if (!e) return s.format('MMM D, YYYY');
  if (s.month() === e.month() && s.year() === e.year()) {
    return `${s.format('MMM D')}–${e.format('D, YYYY')}`;
  }
  return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
}


